/** Book helpers for Value Dashboard trade modal — mirrors GameTable bestBidAsk leg logic. */

import type { GameRow, VenueBook } from './types';

/** Convert an implied probability in cents (1-99) to American odds. Underdog
 *  ( <50¢ ) → "+NNN"; favorite ( >50¢ ) → "-NNN"; 50¢ → "+100". Returns "—"
 *  for anything outside the open interval. */
export function americanOdds(cents: number): string {
  if (cents <= 0 || cents >= 100) return '—';
  if (cents < 50) {
    const odds = Math.round(((100 - cents) / cents) * 100);
    return `+${odds}`;
  }
  if (cents > 50) {
    const odds = Math.round((cents / (100 - cents)) * 100);
    return `-${odds}`;
  }
  return '+100';
}

export function bestYesBidCents(book: VenueBook | undefined): number | undefined {
  if (!book?.yes?.length) return undefined;
  return Math.max(...book.yes.map((l) => l.priceCents));
}

export function bestNoBidCents(book: VenueBook | undefined): number | undefined {
  if (!book?.no?.length) return undefined;
  return Math.max(...book.no.map((l) => l.priceCents));
}

export function bestPriceCentsForOrderSide(
  book: VenueBook | undefined,
  orderSide: 'yes' | 'no',
): number | undefined {
  return orderSide === 'yes' ? bestYesBidCents(book) : bestNoBidCents(book);
}

export function opponentTeamCode(game: GameRow, teamSide: 'away' | 'home'): string {
  return teamSide === 'away' ? game.homeCode : game.awayCode;
}

/** Human outcome for the selected market ticker + YES/NO (buy side). */
export function outcomeSideLabel(
  game: GameRow,
  teamSide: 'away' | 'home',
  orderSide: 'yes' | 'no',
): string {
  const teamCode = teamSide === 'away' ? game.awayCode : game.homeCode;
  const oppCode = opponentTeamCode(game, teamSide);
  if (orderSide === 'yes') return `(${teamCode} wins)`;
  return `(${oppCode} wins)`;
}

export function venueBookForTeam(
  game: GameRow,
  teamSide: 'away' | 'home',
  venue: 'kalshi' | 'polymarket',
): VenueBook | undefined {
  return teamSide === 'away'
    ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
    : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);
}
